import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, withCtx as _withCtx, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createBlock as _createBlock, vShow as _vShow, withDirectives as _withDirectives, Transition as _Transition, normalizeClass as _normalizeClass, Fragment as _Fragment } from "vue"

const _hoisted_1 = { class: "absolute inset-0 z-0 h-full w-full" }
const _hoisted_2 = { class: "pointer-events-auto z-[1600] mb-4 ml-4 mr-4 lg:mb-6 lg:ml-6 lg:mr-0" }
const _hoisted_3 = { class: "pointer-events-auto relative z-[1000] flex h-full flex-col rounded-tr-[40px] border-r border-gray-200 bg-white pt-8" }
const _hoisted_4 = { class: "relative flex shrink-0 flex-col border-b border-gray-100 px-6 pb-6" }
const _hoisted_5 = { class: "flex items-end justify-between gap-4" }
const _hoisted_6 = { class: "flex items-end gap-2" }
const _hoisted_7 = {
  key: 0,
  class: "pb-[0.9rem]"
}
const _hoisted_8 = { class: "flex shrink-0 items-center gap-4" }
const _hoisted_9 = { class: "scrollbar-hide grow overflow-y-auto bg-gray-50 p-6 pb-28 lg:pb-32" }
const _hoisted_10 = { key: 0 }
const _hoisted_11 = {
  key: 1,
  class: "mt-8 flex flex-col items-center justify-center space-y-4 text-center"
}
const _hoisted_12 = {
  key: 1,
  class: "mt-32"
}
const _hoisted_13 = { class: "px-4" }
const _hoisted_14 = { class: "relative flex w-full flex-col gap-3 px-4 pb-4" }
const _hoisted_15 = { class: "flex items-end gap-2" }
const _hoisted_16 = { class: "flex items-end gap-2" }
const _hoisted_17 = { key: 0 }
const _hoisted_18 = { class: "flex items-center justify-between gap-2" }
const _hoisted_19 = { class: "scrollbar-hide grow overflow-y-auto rounded-t-2xl bg-gray-50 p-4 pb-36" }
const _hoisted_20 = { key: 0 }
const _hoisted_21 = {
  key: 1,
  class: "mt-8 flex grow flex-col items-center justify-center space-y-4 text-center"
}
const _hoisted_22 = { class: "flex items-center space-x-2" }

import { onMounted, onUnmounted, ref, computed } from "vue";
import { storeToRefs } from "pinia";

import { useLocationsStore } from "~/js/store/locations";
import { Translations } from "~/js/interfaces/translations";
import { getTranslation, getGlobalTranslation } from "~/js/composables/translations";
import { useSetQueryParams } from "~/js/composables/query-parameters";

import MapLocations from "~/vue/organisms/maps/MapLocations.vue";
import GridLocations from "~/vue/organisms/grids/GridLocations.vue";
import GridSkeleton from "~/vue/organisms/grids/GridSkeleton.vue";
import TextH3 from "~/vue/atoms/texts/TextH3.vue";
import TextBody from "~/vue/atoms/texts/TextBody.vue";
import ButtonPrimary from "~/vue/atoms/buttons/ButtonPrimary.vue";
import ButtonQuarternary from "~/vue/atoms/buttons/ButtonQuarternary.vue";
import SvgLoader from "~/vue/atoms/svgs/SvgLoader.vue";
import FilterLocations from "~/vue/organisms/filters/FilterLocations.vue";
import SortLocations from "~/vue/organisms/filters/SortLocations.vue";

interface Props {
	translations?: Translations | null;
	contactUrl?: string | null;
}


export default /*@__PURE__*/_defineComponent({
  __name: 'ViewLocations',
  props: {
    translations: { default: null },
    contactUrl: { default: null }
  },
  setup(__props: any) {

const props = __props;

const locationsStore = useLocationsStore();
const { locations, loading, pagination, view } = storeToRefs(locationsStore);

const isDesktop = ref(typeof window !== "undefined" ? window.innerWidth >= 1024 : false);
const isTabletSidebarMode = ref(
	typeof window !== "undefined" ? window.innerWidth >= 640 && window.innerWidth < 1024 : false,
);
const sidebarOpen = ref(true);
const popupOpen = ref(false);

const onClearFilter = () => {
	locationsStore.clearFilters();
};

const filteredLocations = computed(() => {
	if (!locations.value) return [];
	return locations.value;
});

// Toggle Button Logic
const handleToggleAction = () => {
	if (isDesktop.value) {
		sidebarOpen.value = !sidebarOpen.value;
		return;
	}
	const nextView = view.value === "list" ? "map" : "list";
	locationsStore.toggleView(nextView);
};

const toggleButtonText = computed(() => {
	if (isDesktop.value) {
		return sidebarOpen.value
			? getTranslation(props.translations, "Show map")
			: getTranslation(props.translations, "Show list");
	}
	return view.value === "list"
		? getTranslation(props.translations, "Show map")
		: getTranslation(props.translations, "Show list");
});

const toggleButtonIcon = computed(() => {
	if (isDesktop.value) {
		return sidebarOpen.value ? "map" : "list";
	}
	return view.value === "list" ? "map" : "list";
});

const rootClasses = computed(() => {
	return [
		"w-full relative bg-gray-100 flex flex-col grow h-screen min-h-[600px] overflow-hidden",
		popupOpen.value ? "popup-is-open" : "",
	].join(" ");
});

const desktopSidebarWidthClass = computed(() => (popupOpen.value ? "w-[58%]" : "w-2/3"));

const desktopToggleButtonLeftClass = computed(() =>
	popupOpen.value ? "left-[calc(58%+21%)]" : "left-[calc(66.666667%+16.666667%)]",
);

const updateMedia = () => {
	if (typeof window !== "undefined") {
		isDesktop.value = window.innerWidth >= 1024;
		isTabletSidebarMode.value = window.innerWidth >= 640 && window.innerWidth < 1024;

		// Enforce map view on desktop if it was left in list view
		if (isDesktop.value && view.value === "list") {
			locationsStore.toggleView("map");
		}
	}
};

onMounted(async () => {
	updateMedia();
	if (typeof window !== "undefined") {
		window.addEventListener("resize", updateMedia);
	}

	if (isDesktop.value) {
		locationsStore.toggleView("map");
		sidebarOpen.value = true;
	}

	await locationsStore.fetch(true, useSetQueryParams());
});

onUnmounted(() => {
	if (typeof window !== "undefined") {
		window.removeEventListener("resize", updateMedia);
	}
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", {
    class: _normalizeClass(`${rootClasses.value} isolate`)
  }, [
    (isDesktop.value || _unref(view) === 'map')
      ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
          _createElementVNode("div", _hoisted_1, [
            _createElementVNode("div", {
              class: _normalizeClass(["pointer-events-none absolute left-0 z-[1000] transition-all duration-300 ease-in-out", isDesktop.value ? ['top-32 bottom-14', desktopSidebarWidthClass.value] : 'top-32 w-full'])
            }, [
              _createElementVNode("div", _hoisted_2, [
                _createVNode(FilterLocations, {
                  variant: "search-bar",
                  translations: __props.translations
                }, null, 8, ["translations"])
              ]),
              _createVNode(_Transition, { name: "slide" }, {
                default: _withCtx(() => [
                  _withDirectives(_createElementVNode("section", _hoisted_3, [
                    _createElementVNode("div", _hoisted_4, [
                      _createElementVNode("div", _hoisted_5, [
                        _createElementVNode("div", _hoisted_6, [
                          _createElementVNode("h1", null, [
                            _createVNode(TextH3, null, {
                              default: _withCtx(() => [
                                _createTextVNode(_toDisplayString(_unref(getTranslation)(__props.translations, "Locations")), 1)
                              ]),
                              _: 1
                            })
                          ]),
                          _createVNode(TextBody, { utilities: "self-end leading-none text-concrete-700" }, {
                            default: _withCtx(() => [
                              (!_unref(loading) && _unref(locations))
                                ? (_openBlock(), _createElementBlock("span", _hoisted_7, _toDisplayString(filteredLocations.value.length), 1))
                                : _createCommentVNode("", true),
                              (_unref(loading))
                                ? (_openBlock(), _createBlock(SvgLoader, {
                                    key: 1,
                                    utilities: "mb-2"
                                  }))
                                : _createCommentVNode("", true)
                            ]),
                            _: 1
                          })
                        ]),
                        _createElementVNode("div", _hoisted_8, [
                          _createVNode(SortLocations, { translations: __props.translations }, null, 8, ["translations"])
                        ])
                      ])
                    ]),
                    _createElementVNode("div", _hoisted_9, [
                      (_unref(locations))
                        ? (_openBlock(), _createElementBlock("div", _hoisted_10, [
                            (_unref(loading))
                              ? (_openBlock(), _createBlock(GridSkeleton, {
                                  key: 0,
                                  size: _unref(pagination).itemsPerPage
                                }, null, 8, ["size"]))
                              : _createCommentVNode("", true),
                            (_unref(locations) && !_unref(loading))
                              ? (_openBlock(), _createBlock(GridLocations, {
                                  key: 1,
                                  locations: filteredLocations.value,
                                  translations: __props.translations,
                                  class: "!grid-cols-1 space-y-2",
                                  "is-sidebar": isDesktop.value
                                }, null, 8, ["locations", "translations", "is-sidebar"]))
                              : _createCommentVNode("", true)
                          ]))
                        : _createCommentVNode("", true),
                      (!_unref(loading) && filteredLocations.value.length === 0)
                        ? (_openBlock(), _createElementBlock("div", _hoisted_11, [
                            _createVNode(TextH3, { utilities: "inline-flex items-center space-x-4" }, {
                              default: _withCtx(() => [
                                _cache[3] || (_cache[3] = _createElementVNode("span", { class: "text-lg" }, [
                                  _createElementVNode("i", { class: "fas fa-warehouse" })
                                ], -1)),
                                _createElementVNode("span", null, _toDisplayString(_unref(getGlobalTranslation)("No results")), 1)
                              ]),
                              _: 1
                            }),
                            _createVNode(TextBody, { utilities: "text-center max-w-md text-sm" }, {
                              default: _withCtx(() => [
                                _createTextVNode(_toDisplayString(_unref(getTranslation)(__props.translations, "We haven't found any locations in this area.")), 1)
                              ]),
                              _: 1
                            })
                          ]))
                        : _createCommentVNode("", true)
                    ])
                  ], 512), [
                    [_vShow, isDesktop.value && sidebarOpen.value]
                  ])
                ]),
                _: 1
              })
            ], 2),
            _createVNode(MapLocations, {
              locations: _unref(locations),
              translations: __props.translations,
              utilities: "h-full w-full",
              "has-toggle": !isDesktop.value,
              "sidebar-open": isDesktop.value && sidebarOpen.value,
              "desktop-sidebar-ratio": popupOpen.value ? 0.58 : 2 / 3,
              "is-desktop": isDesktop.value,
              onPopupChange: _cache[0] || (_cache[0] = ($event: any) => (popupOpen.value = $event))
            }, null, 8, ["locations", "translations", "has-toggle", "sidebar-open", "desktop-sidebar-ratio", "is-desktop"])
          ]),
          (isDesktop.value)
            ? (_openBlock(), _createElementBlock("div", {
                key: 0,
                class: _normalizeClass(["absolute bottom-13 -translate-x-1/2 transition-all duration-500 ease-in-out", [sidebarOpen.value ? desktopToggleButtonLeftClass.value : 'left-1/2', 'z-10']])
              }, [
                (_openBlock(), _createBlock(ButtonPrimary, {
                  key: `desktop-${String(sidebarOpen.value)}`,
                  size: "small",
                  icon: toggleButtonIcon.value,
                  type: "button",
                  "icon-position": "after",
                  content: toggleButtonText.value,
                  utilities: "shadow-lg",
                  onClick: handleToggleAction
                }, null, 8, ["icon", "content"]))
              ], 2))
            : _createCommentVNode("", true)
        ], 64))
      : _createCommentVNode("", true),
    (!isDesktop.value && _unref(view) === 'list')
      ? (_openBlock(), _createElementBlock("div", _hoisted_12, [
          _createElementVNode("div", _hoisted_13, [
            _createVNode(FilterLocations, {
              variant: "search-bar",
              translations: __props.translations
            }, null, 8, ["translations"])
          ]),
          _createElementVNode("section", {
            class: _normalizeClass(["mx-4 mb-0 mt-4 flex h-[calc(100vh-11rem)] flex-col overflow-hidden rounded-t-3xl bg-white pt-4", isTabletSidebarMode.value ? 'w-auto max-w-none' : ''])
          }, [
            _createElementVNode("div", _hoisted_14, [
              _createElementVNode("div", _hoisted_15, [
                _createElementVNode("div", _hoisted_16, [
                  _createElementVNode("h1", null, [
                    _createVNode(TextH3, null, {
                      default: _withCtx(() => [
                        _createTextVNode(_toDisplayString(_unref(getTranslation)(__props.translations, "Locations")), 1)
                      ]),
                      _: 1
                    })
                  ]),
                  _createVNode(TextBody, { utilities: "pb-0.5 text-concrete-700" }, {
                    default: _withCtx(() => [
                      (!_unref(loading) && _unref(locations))
                        ? (_openBlock(), _createElementBlock("span", _hoisted_17, _toDisplayString(_unref(locations).length), 1))
                        : _createCommentVNode("", true),
                      (_unref(loading))
                        ? (_openBlock(), _createBlock(SvgLoader, {
                            key: 1,
                            utilities: "mb-2"
                          }))
                        : _createCommentVNode("", true)
                    ]),
                    _: 1
                  })
                ])
              ]),
              _createElementVNode("div", _hoisted_18, [
                _createVNode(SortLocations, {
                  class: "min-w-0 justify-start",
                  translations: __props.translations
                }, null, 8, ["translations"])
              ])
            ]),
            _createElementVNode("div", _hoisted_19, [
              (_unref(locations))
                ? (_openBlock(), _createElementBlock("div", _hoisted_20, [
                    (_unref(loading))
                      ? (_openBlock(), _createBlock(GridSkeleton, {
                          key: 0,
                          size: _unref(pagination).itemsPerPage
                        }, null, 8, ["size"]))
                      : _createCommentVNode("", true),
                    (_unref(locations) && !_unref(loading))
                      ? (_openBlock(), _createBlock(GridLocations, {
                          key: 1,
                          locations: filteredLocations.value,
                          translations: __props.translations,
                          "is-sidebar": true,
                          class: "!grid-cols-1 space-y-2"
                        }, null, 8, ["locations", "translations"]))
                      : _createCommentVNode("", true)
                  ]))
                : _createCommentVNode("", true),
              (_unref(locations) && _unref(locations).length === 0 && !_unref(loading))
                ? (_openBlock(), _createElementBlock("div", _hoisted_21, [
                    _createElementVNode("div", null, [
                      _createVNode(TextH3, { utilities: "inline-flex items-center space-x-4" }, {
                        default: _withCtx(() => [
                          _cache[4] || (_cache[4] = _createElementVNode("span", { class: "text-lg" }, [
                            _createElementVNode("i", { class: "fas fa-warehouse" })
                          ], -1)),
                          _createElementVNode("span", null, _toDisplayString(_unref(getGlobalTranslation)("No results")), 1)
                        ]),
                        _: 1
                      })
                    ]),
                    _createVNode(TextBody, { utilities: "text-center max-w-md" }, {
                      default: _withCtx(() => [
                        _createTextVNode(_toDisplayString(_unref(getTranslation)(
								__props.translations,
								"We haven't found any locations with this combination. If you have questions about our available locations, you can contact us. Clear the filters to see all our available locations",
							)), 1)
                      ]),
                      _: 1
                    }),
                    _createElementVNode("div", _hoisted_22, [
                      (__props.contactUrl)
                        ? (_openBlock(), _createBlock(ButtonPrimary, {
                            key: 0,
                            content: _unref(getGlobalTranslation)('Contact us'),
                            size: "small",
                            url: __props.contactUrl,
                            type: "a"
                          }, null, 8, ["content", "url"]))
                        : _createCommentVNode("", true),
                      _createVNode(ButtonQuarternary, {
                        content: _unref(getGlobalTranslation)('Clear filters'),
                        size: "small",
                        onClick: _cache[1] || (_cache[1] = ($event: any) => (onClearFilter()))
                      }, null, 8, ["content"])
                    ])
                  ]))
                : _createCommentVNode("", true)
            ]),
            _createVNode(ButtonPrimary, {
              size: "small",
              icon: "map",
              type: "button",
              "icon-position": "after",
              content: _unref(getTranslation)(__props.translations, 'View map'),
              utilities: "fixed left-1/2 -translate-x-1/2 bottom-13 z-10 shadow-lg",
              onClick: _cache[2] || (_cache[2] = () => _unref(locationsStore).toggleView('map'))
            }, null, 8, ["content"])
          ], 2)
        ]))
      : _createCommentVNode("", true)
  ], 2))
}
}

})